import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { FormField, HpButton, HpCardHeader, HpTag, TableScroll } from '@hp/ui';
import { useCan } from '../../api/me';
import { trpc } from '../../api/trpc';
import { ConfirmDialog } from '../../components/confirm-dialog';
import { ErrorAlert } from '../../components/error-alert';
import { RelativeTime } from '../../components/relative-time';
import { Spinner } from '../../components/ui/icons';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import { domainErrorOf, fmtDateTime, fmtInt, prettyJson } from '../../lib/format';
import { MARKET_DEFAULTS_ANCHOR } from './defaults-card';
import { useLeadErrorText } from './error-text';
import { isLeadStatus, isOwnerSource, isRejectReason, LEAD_STATUS_TONE } from './labels';
import {
  invalidateLeads,
  useChannels,
  useOwners,
  usePipelines,
  type Channel,
  type ChannelMode,
  type MetaSubscriptionRow,
  type SendTestResult,
} from './queries';

/** The confirm dialog the card is showing (one at a time). */
type Confirm = { kind: 'rotate' | 'live' | 'subscribe'; channel: Channel } | null;

/** Read-only monospace field + Copy (clipboard API; where it is refused, the text is selected for Ctrl+C). */
function CopyField({ value, label }: { value: string; label: string }) {
  const { t } = useTranslation(['leads', 'common']);
  const input = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<'idle' | 'copied' | 'selected'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const flash = (next: 'copied' | 'selected') => {
    setState(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setState('idle'), 2000);
  };
  const selectAll = () => {
    input.current?.focus();
    input.current?.select();
  };
  const copy = () => {
    const fallback = () => {
      selectAll();
      flash('selected');
    };
    if (!navigator.clipboard) return fallback();
    navigator.clipboard.writeText(value).then(() => flash('copied'), fallback);
  };
  return (
    <div className="bo-copy-field">
      <input
        ref={input}
        className="bo-url-input"
        readOnly
        value={value}
        aria-label={label}
        onFocus={(e) => e.target.select()}
      />
      <HpButton className="bo-button--sm" icon={state === 'copied' ? 'check' : 'copy'} onClick={copy}>
        {state === 'copied' ? t('general.channels.copied') : t('common:actions.copy')}
      </HpButton>
      {state === 'selected' && (
        <small className="bo-hint" role="status">
          {t('general.channels.copySelected')}
        </small>
      )}
    </div>
  );
}

function ModeTag({ mode }: { mode: ChannelMode }) {
  const { t } = useTranslation(['leads']);
  return (
    <HpTag tone={mode === 'live' ? 'success' : 'neutral'} title={t(`general.channels.modeTitle.${mode}`)}>
      {t(`general.channels.mode.${mode}`)}
    </HpTag>
  );
}

function EnabledTag({ enabled }: { enabled: boolean }) {
  const { t } = useTranslation(['leads']);
  return (
    <HpTag tone={enabled ? 'info' : 'warning'}>
      {t(enabled ? 'general.channels.enabled' : 'general.channels.disabled')}
    </HpTag>
  );
}

/** Mode, enabled, last event, events in 24 h and the last refused body. */
function ChannelStatus({ channel, canManage }: { channel: Channel; canManage: boolean }) {
  const { t } = useTranslation(['leads']);
  const reason = channel.lastRejectedReason;
  return (
    <div className="bo-sections bo-sections--tight">
      <dl className="bo-facts">
        <div>
          <dt>{t('general.channels.status.mode')}</dt>
          <dd>
            <ModeTag mode={channel.mode} /> <EnabledTag enabled={channel.enabled} />
          </dd>
        </div>
        <div>
          <dt>{t('general.channels.status.lastEvent')}</dt>
          <dd>
            {channel.lastEventAt ? (
              <RelativeTime value={channel.lastEventAt} />
            ) : (
              <span className="bo-hint--danger">{t('general.channels.status.never')}</span>
            )}
          </dd>
        </div>
        <div>
          <dt>{t('general.channels.status.events24h')}</dt>
          <dd className="bo-mono">{fmtInt(channel.events24h)}</dd>
        </div>
        <div>
          <dt>{t('general.channels.status.eventsTotal')}</dt>
          <dd className="bo-mono">{fmtInt(channel.eventCount)}</dd>
        </div>
        <div>
          <dt>{t('general.channels.status.lastRejected')}</dt>
          <dd>
            {channel.lastRejectedAt ? (
              <>
                <RelativeTime value={channel.lastRejectedAt} />
                {reason && (
                  <>
                    {' · '}
                    {isRejectReason(reason)
                      ? t(`general.channels.rejectReason.${reason}`)
                      : t('general.channels.rejectReasonOther', { reason })}
                  </>
                )}
              </>
            ) : (
              <span className="bo-muted">—</span>
            )}
          </dd>
        </div>
      </dl>
      {canManage && channel.lastRejectedAt && channel.lastRejectedPayload !== null && (
        <details className="bo-payload">
          <summary>{t('general.channels.rejectedPayload')}</summary>
          <pre className="bo-code">{prettyJson(channel.lastRejectedPayload)}</pre>
        </details>
      )}
    </div>
  );
}

/** One line for a test lead's outcome: status + what the router decided (owner, source, target). */
function TestResult({ result }: { result: SendTestResult }) {
  const { t } = useTranslation(['leads']);
  const owners = useOwners();
  const pipelines = usePipelines();
  const outcome = result.decision?.outcome;
  let line: string | null = null;
  if (outcome?.kind === 'routed') {
    const owner = owners.data?.find((o) => o.pdId === outcome.ownerUserPdId)?.name;
    const pipeline = pipelines.data?.find((p) => p.pdId === outcome.pipelinePdId);
    const stage = pipeline?.stages.find((s) => s.pdId === outcome.stagePdId)?.name;
    line = t('general.channels.test.routed', {
      owner: owner ?? t('rules.unknownId', { id: outcome.ownerUserPdId }),
      source: isOwnerSource(outcome.ownerSource)
        ? t(`general.ownerSource.${outcome.ownerSource}`)
        : outcome.ownerSource,
      pipeline: pipeline?.name ?? t('rules.unknownId', { id: outcome.pipelinePdId }),
      stage: stage ?? t('rules.unknownId', { id: outcome.stagePdId }),
    });
  } else if (outcome?.kind === 'needs_rule') {
    line = t('general.channels.test.needsRule', { reason: outcome.reason });
  } else if (outcome?.kind === 'duplicate') {
    line = t('general.channels.test.duplicate');
  } else if (result.reason) {
    line = t('general.channels.test.reason', { reason: result.reason });
  }
  return (
    <p className="bo-row" role="status">
      <HpTag tone={isLeadStatus(result.status) ? LEAD_STATUS_TONE[result.status] : 'neutral'}>
        {isLeadStatus(result.status) ? t(`general.leadStatus.${result.status}`) : result.status}
      </HpTag>
      {line && <span>{line}</span>}
      <Link to={`/marketing/leads?tab=received&lead=${encodeURIComponent(result.leadId)}`}>
        {t('general.channels.test.open')}
      </Link>
    </p>
  );
}

/** "Send test lead": a synthetic lead through the real endpoint; shadow channels only, never to Pipedrive. */
function TestLeadForm({ channel, onClose }: { channel: Channel; onClose: () => void }) {
  const { t } = useTranslation(['leads', 'common']);
  const describe = useLeadErrorText();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const send = useMutation(trpc.leads.channels.sendTest.mutationOptions({ onSettled: invalidateLeads }));
  const allowed = channel.mode === 'shadow' && channel.enabled;
  return (
    <div className="bo-subform">
      <p className="bo-subform__title">{t('general.channels.test.title')}</p>
      <p className="bo-hint">{t('general.channels.test.hint')}</p>
      <form
        className="bo-inline-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (!allowed) return;
          send.mutate({
            code: channel.code,
            name: name.trim() || undefined,
            email: email.trim() || undefined,
            phone: phone.trim() || undefined,
          });
        }}
      >
        <FormField label={t('general.channels.test.name')}>
          <input
            value={name}
            placeholder={t('general.channels.test.namePlaceholder')}
            onChange={(e) => setName(e.target.value)}
          />
        </FormField>
        <FormField label={t('general.channels.test.email')}>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </FormField>
        <FormField label={t('general.channels.test.phone')}>
          <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </FormField>
        <div className="bo-inline-form__submit bo-row">
          <HpButton type="submit" variant="primary" icon="play" busy={send.isPending} disabled={!allowed}>
            {t('general.channels.test.send')}
          </HpButton>
          <HpButton onClick={onClose} disabled={send.isPending}>
            {t('common:actions.close')}
          </HpButton>
        </div>
      </form>
      {!allowed && <p className="bo-hint">{t('general.channels.test.onlyShadow')}</p>}
      <ErrorAlert error={send.error} describe={describe} />
      {send.data && <TestResult result={send.data} />}
    </div>
  );
}

/** Buttons a `leads.manage` holder has for one channel; `extra` adds kind-specific actions. */
function ChannelActions({
  channel,
  busy,
  onEnabled,
  onMode,
  extra,
}: {
  channel: Channel;
  busy: boolean;
  onEnabled: (enabled: boolean) => void;
  onMode: (mode: ChannelMode) => void;
  extra?: ReactNode;
}) {
  const { t } = useTranslation(['leads']);
  const liveBlocked = channel.mode === 'shadow' && !channel.marketDefaultComplete;
  return (
    <div className="bo-sections bo-sections--tight">
      <span className="bo-row">
        <HpButton className="bo-button--sm" disabled={busy} onClick={() => onEnabled(!channel.enabled)}>
          {t(channel.enabled ? 'general.channels.disable' : 'general.channels.enable')}
        </HpButton>
        <HpButton
          className="bo-button--sm"
          icon={channel.mode === 'shadow' ? 'play' : 'pause'}
          disabled={busy || liveBlocked}
          onClick={() => onMode(channel.mode === 'shadow' ? 'live' : 'shadow')}
        >
          {t(channel.mode === 'shadow' ? 'general.channels.goLive' : 'general.channels.goShadow')}
        </HpButton>
        {extra}
      </span>
      {liveBlocked && (
        <span className="bo-row">
          <small className="bo-hint">{t('general.channels.liveBlocked')}</small>
          <DefaultsLink />
        </span>
      )}
    </div>
  );
}

/** A test lead goes only to an enabled channel in shadow mode (the API refuses anything else). */
const testable = (channel: Channel) => channel.mode === 'shadow' && channel.enabled;

/** Fields the web normalizer reads (labels: `leads:general.channels.payload.field.<key>`), in display order. */
const PAYLOAD_FIELDS = ['name', 'email', 'phone', 'projekt', 'zdroj', 'kampan', 'note', 'event_id'] as const;

/** A sample request: the sender's real form fields are posted as JSON or as a form (`application/x-www-form-urlencoded`). */
function exampleCurl(url: string): string {
  const body = {
    name: 'Jan Novák',
    email: 'jan.novak@example.com',
    phone: '+420 777 123 456',
    projekt: 'Rezidence Example',
    zdroj: 'web',
    kampan: 'Example campaign',
    note: 'Example message',
  };
  return `curl -X POST '${url}' \\\n  -H 'Content-Type: application/json' \\\n  -d '${JSON.stringify(body, null, 2)}'`;
}

/** Plan 8.3: what a web sender may post, and an example request (collapsed). */
function PayloadHelp({ url }: { url: string | null }) {
  const { t } = useTranslation(['leads']);
  return (
    <details className="bo-payload">
      <summary>{t('general.channels.payload.summary')}</summary>
      <div className="bo-sections bo-sections--tight">
        <p className="bo-hint">{t('general.channels.payload.intro')}</p>
        <dl className="bo-facts bo-facts--stacked">
          {PAYLOAD_FIELDS.map((key) => (
            <div key={key}>
              <dt className="bo-mono">{key}</dt>
              <dd>{t(`general.channels.payload.field.${key}`)}</dd>
            </div>
          ))}
        </dl>
        <p className="bo-hint">{t('general.channels.payload.other')}</p>
        {url && (
          <>
            <p className="bo-strong">{t('general.channels.payload.curl')}</p>
            <pre className="bo-code">{exampleCurl(url)}</pre>
          </>
        )}
      </div>
    </details>
  );
}

/** Points at the Market defaults card (a channel goes live only with a complete default). */
function DefaultsLink() {
  const { t } = useTranslation(['leads']);
  return (
    <HpButton
      variant="quiet"
      icon="arrowRight"
      className="bo-button--sm"
      onClick={() => {
        const card = document.getElementById(MARKET_DEFAULTS_ANCHOR);
        card?.scrollIntoView({ block: 'start', behavior: 'smooth' });
        card?.focus({ preventScroll: true });
      }}
    >
      {t('general.channels.openDefaults')}
    </HpButton>
  );
}

/** Card "Webhook URLs": where each sender posts, whether anything arrived, and the channel switches. */
export function ChannelsCard() {
  const { t } = useTranslation(['leads', 'common']);
  const describe = useLeadErrorText();
  const canManage = useCan()('leads.manage');
  const channels = useChannels();
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [testing, setTesting] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<MetaSubscriptionRow[] | null>(null);

  const setEnabled = useMutation(trpc.leads.channels.setEnabled.mutationOptions({ onSettled: invalidateLeads }));
  const setMode = useMutation(
    trpc.leads.channels.setMode.mutationOptions({
      onSettled: () => {
        setConfirm(null);
        return invalidateLeads();
      },
    }),
  );
  const rotate = useMutation(
    trpc.leads.channels.rotateToken.mutationOptions({
      onSettled: () => {
        setConfirm(null);
        return invalidateLeads();
      },
    }),
  );
  const check = useMutation(
    trpc.leads.channels.checkMetaSubscription.mutationOptions({ onSuccess: (rows) => setSubscription(rows) }),
  );
  const subscribe = useMutation(
    trpc.leads.channels.subscribeMetaPage.mutationOptions({
      onSuccess: (row) => setSubscription((rows) => rows?.map((r) => (r.code === row.code ? row : r)) ?? [row]),
      onSettled: () => {
        setConfirm(null);
        return invalidateLeads();
      },
    }),
  );

  /** A new channel action starts clean: only the latest action's error stays under its channel. */
  const clearErrors = () => {
    setEnabled.reset();
    setMode.reset();
    rotate.reset();
    subscribe.reset();
  };
  const ask = (next: NonNullable<Confirm>) => {
    clearErrors();
    setConfirm(next);
  };
  const changeMode = (channel: Channel, mode: ChannelMode) => {
    clearErrors();
    // Live writes to Pipedrive: confirm first. Back to shadow is always safe.
    if (mode === 'live') setConfirm({ kind: 'live', channel });
    else setMode.mutate({ code: channel.code, mode });
  };
  const busyFor = (code: string) =>
    (setEnabled.isPending && setEnabled.variables?.code === code) ||
    (setMode.isPending && setMode.variables?.code === code) ||
    (rotate.isPending && rotate.variables?.code === code);
  /** The error of the last channel action, shown under the channel it was about. */
  const errorFor = (code: string) =>
    (setEnabled.variables?.code === code ? setEnabled.error : null) ??
    (setMode.variables?.code === code ? setMode.error : null) ??
    (rotate.variables?.code === code ? rotate.error : null) ??
    (subscribe.variables?.code === code ? subscribe.error : null);
  // The list may still say "complete" when the API already refused (a race): then the error carries the link.
  const incompleteError = (channel: Channel) =>
    channel.marketDefaultComplete &&
    setMode.variables?.code === channel.code &&
    domainErrorOf(setMode.error)?.code === 'LEAD_CHANNEL_DEFAULT_INCOMPLETE';

  const all = channels.data ?? [];
  const web = all.filter((c) => c.kind === 'web');
  const meta = all.filter((c) => c.kind === 'meta_page');

  const actionsFor = (channel: Channel, extra?: ReactNode) =>
    canManage && (
      <ChannelActions
        channel={channel}
        busy={busyFor(channel.code)}
        onEnabled={(enabled) => {
          clearErrors();
          setEnabled.mutate({ code: channel.code, enabled });
        }}
        onMode={(mode) => changeMode(channel, mode)}
        extra={extra}
      />
    );
  const errorsFor = (channel: Channel) => (
    <>
      <ErrorAlert error={errorFor(channel.code)} describe={describe} />
      {incompleteError(channel) && <DefaultsLink />}
    </>
  );

  return (
    <section className="hp-card">
      <HpCardHeader title={t('general.channels.title')} subtitle={t('general.channels.subtitle')} icon="link" />
      <ErrorAlert error={channels.error} className="bo-alert--inset" />
      {channels.isLoading && (
        <p className="hp-card__body">
          <Spinner>{t('common:actions.loading')}</Spinner>
        </p>
      )}
      <div className="hp-card__body bo-sections">
        {web.map((channel) => (
          <fieldset key={channel.code} className="bo-fieldset">
            <legend>
              {channel.label} <small className="bo-muted bo-mono">{channel.code}</small>
            </legend>
            <div className="bo-sections bo-sections--tight">
              <small className="bo-hint">
                {t('general.channels.pasteHint', {
                  hint: channel.pasteHint,
                  market: channel.marketLabel ?? channel.marketCode,
                })}
              </small>
              {channel.url &&
                (canManage ? (
                  <CopyField value={channel.url} label={t('general.channels.urlLabel', { channel: channel.label })} />
                ) : (
                  <>
                    <input
                      className="bo-url-input"
                      readOnly
                      value={channel.url}
                      aria-label={t('general.channels.urlLabel', { channel: channel.label })}
                    />
                    <small className="bo-hint">{t('general.channels.urlMasked')}</small>
                  </>
                ))}
              {!channel.url && <small className="bo-hint bo-hint--danger">{t('general.channels.noToken')}</small>}
              {channel.previousTokenExpiresAt && (
                <small className="bo-hint">
                  {t('general.channels.previousValid', { at: fmtDateTime(channel.previousTokenExpiresAt) })}
                </small>
              )}
              <PayloadHelp url={channel.url} />
              <ChannelStatus channel={channel} canManage={canManage} />
              {actionsFor(
                channel,
                <>
                  <HpButton
                    className="bo-button--sm"
                    icon="key"
                    disabled={busyFor(channel.code)}
                    onClick={() => ask({ kind: 'rotate', channel })}
                  >
                    {t('general.channels.rotate')}
                  </HpButton>
                  <HpButton
                    className="bo-button--sm"
                    icon="mail"
                    disabled={testing === channel.code || !testable(channel)}
                    onClick={() => setTesting(channel.code)}
                  >
                    {t('general.channels.test.openForm')}
                  </HpButton>
                </>,
              )}
              {canManage && !testable(channel) && (
                <small className="bo-hint">{t('general.channels.test.onlyShadow')}</small>
              )}
              {errorsFor(channel)}
              {canManage && testing === channel.code && (
                <TestLeadForm channel={channel} onClose={() => setTesting(null)} />
              )}
            </div>
          </fieldset>
        ))}

        {meta.length > 0 && (
          <MetaBlock
            channels={meta}
            canManage={canManage}
            subscription={subscription}
            checking={check.isPending}
            checkError={check.error}
            onCheck={() => check.mutate()}
            onSubscribe={(channel) => ask({ kind: 'subscribe', channel })}
            actionsFor={actionsFor}
            errorsFor={errorsFor}
          />
        )}
      </div>

      <ConfirmDialog
        open={confirm !== null}
        title={confirm ? t(`general.channels.confirm.${confirm.kind}.title`, { channel: confirm.channel.label }) : ''}
        confirmLabel={confirm ? t(`general.channels.confirm.${confirm.kind}.confirm`) : ''}
        busy={setMode.isPending || rotate.isPending || subscribe.isPending}
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (!confirm) return;
          const code = confirm.channel.code;
          if (confirm.kind === 'rotate') rotate.mutate({ code });
          else if (confirm.kind === 'live') setMode.mutate({ code, mode: 'live' });
          else subscribe.mutate({ code });
        }}
      >
        {confirm && (
          <p>
            {t(`general.channels.confirm.${confirm.kind}.body`, {
              channel: confirm.channel.label,
              market: confirm.channel.marketLabel ?? confirm.channel.marketCode,
              page: confirm.channel.metaPageId ?? '—',
            })}
          </p>
        )}
      </ConfirmDialog>
    </section>
  );
}

/** Meta's half of the card: one callback URL for both pages, the page → market table and the subscription. */
function MetaBlock({
  channels,
  canManage,
  subscription,
  checking,
  checkError,
  onCheck,
  onSubscribe,
  actionsFor,
  errorsFor,
}: {
  channels: Channel[];
  canManage: boolean;
  subscription: MetaSubscriptionRow[] | null;
  checking: boolean;
  checkError: unknown;
  onCheck: () => void;
  onSubscribe: (channel: Channel) => void;
  actionsFor: (channel: Channel) => ReactNode;
  errorsFor: (channel: Channel) => ReactNode;
}) {
  const { t } = useTranslation(['leads', 'common']);
  const describe = useLeadErrorText();
  // Every Meta row carries the same shared values.
  const first = channels[0];
  const callbackUrl = first?.callbackUrl ?? null;
  const graphConfigured = first?.graphConfigured === true;
  const webhookConfigured = first?.webhookConfigured === true;
  const notConfigured = !graphConfigured || domainErrorOf(checkError)?.code === 'LEAD_META_NOT_CONFIGURED';

  return (
    <fieldset className="bo-fieldset">
      <legend>{t('general.meta.title')}</legend>
      <div className="bo-sections bo-sections--tight">
        <small className="bo-hint">{first?.pasteHint}</small>
        {callbackUrl && <CopyField value={callbackUrl} label={t('general.meta.callbackUrl')} />}
        <dl className="bo-facts">
          <div>
            <dt>{t('general.meta.verifyToken')}</dt>
            <dd>
              <HpTag tone={first?.verifyTokenConfigured ? 'success' : 'warning'}>
                {t(first?.verifyTokenConfigured ? 'general.meta.yes' : 'general.meta.no')}
              </HpTag>
            </dd>
          </div>
          <div>
            <dt>{t('general.meta.endpoint')}</dt>
            <dd>
              <HpTag tone={webhookConfigured ? 'success' : 'warning'}>
                {t(webhookConfigured ? 'general.meta.yes' : 'general.meta.no')}
              </HpTag>
            </dd>
          </div>
          <div>
            <dt>{t('general.meta.graph')}</dt>
            <dd>
              <HpTag tone={graphConfigured ? 'success' : 'warning'}>
                {t(graphConfigured ? 'general.meta.yes' : 'general.meta.no')}
              </HpTag>
            </dd>
          </div>
        </dl>
        <small className="bo-hint">{t('general.meta.verifyTokenHint')}</small>
        {!webhookConfigured && <p className="bo-hint bo-hint--danger">{t('general.meta.endpointMissing')}</p>}
        {notConfigured && <p className="bo-hint bo-hint--danger">{t('general.meta.notConfigured')}</p>}

        <TableScroll label={t('general.meta.pages')}>
          <BrutTable>
            <BrutTheadRow>
              <th>{t('general.meta.columns.page')}</th>
              <th>{t('general.meta.columns.pageId')}</th>
              <th>{t('general.meta.columns.market')}</th>
              <th>{t('general.meta.columns.status')}</th>
              {!notConfigured && <th>{t('general.meta.columns.subscription')}</th>}
            </BrutTheadRow>
            <tbody>
              {channels.map((channel) => {
                const sub = subscription?.find((s) => s.code === channel.code);
                return (
                  <tr key={channel.code}>
                    <td>
                      <span className="bo-strong">{channel.label}</span>
                      <br />
                      <small className="bo-muted bo-mono">{channel.code}</small>
                    </td>
                    <td className="bo-mono">{channel.metaPageId ?? '—'}</td>
                    <td>{channel.marketLabel ?? channel.marketCode}</td>
                    <td>
                      <div className="bo-sections bo-sections--tight">
                        <ChannelStatus channel={channel} canManage={canManage} />
                        {actionsFor(channel)}
                        {errorsFor(channel)}
                      </div>
                    </td>
                    {!notConfigured && (
                      <td>
                        <SubscriptionCell row={sub} canManage={canManage} onSubscribe={() => onSubscribe(channel)} />
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </BrutTable>
        </TableScroll>
        {!notConfigured && (
          <span className="bo-row">
            <HpButton className="bo-button--sm" icon="refresh" busy={checking} onClick={onCheck}>
              {t('general.meta.check')}
            </HpButton>
            <small className="bo-hint">{t('general.meta.checkHint')}</small>
          </span>
        )}
        <ErrorAlert error={checkError} describe={describe} />

        <div className="bo-sections bo-sections--tight">
          <p className="bo-strong">{t('general.meta.reminder.title')}</p>
          <ol className="bo-steps">
            {(['app', 'pages', 'token', 'callback', 'subscribe', 'mode', 'test'] as const).map((step) => (
              <li key={step}>{t(`general.meta.reminder.${step}`)}</li>
            ))}
          </ol>
        </div>
      </div>
    </fieldset>
  );
}

function SubscriptionCell({
  row,
  canManage,
  onSubscribe,
}: {
  row: MetaSubscriptionRow | undefined;
  canManage: boolean;
  onSubscribe: () => void;
}) {
  const { t } = useTranslation(['leads']);
  if (!row) return <span className="bo-muted">{t('general.meta.notChecked')}</span>;
  if (row.error) {
    return (
      <span className="bo-sections bo-sections--tight">
        <HpTag tone="danger">{t('general.meta.error')}</HpTag>
        <small className="bo-mono">{row.error.code}</small>
        <small>{row.error.message}</small>
        {row.error.missingScope && (
          <small className="bo-hint--danger">{t('general.meta.missingScope', { scope: row.error.missingScope })}</small>
        )}
      </span>
    );
  }
  if (row.subscribed) {
    return (
      <span className="bo-sections bo-sections--tight">
        <HpTag tone="success">{t('general.meta.subscribed')}</HpTag>
        {row.fields.length > 0 && <small className="bo-muted bo-mono">{row.fields.join(', ')}</small>}
      </span>
    );
  }
  return (
    <span className="bo-sections bo-sections--tight">
      <HpTag tone="warning">{t('general.meta.notSubscribed')}</HpTag>
      {canManage && (
        <HpButton className="bo-button--sm" icon="bell" onClick={onSubscribe}>
          {t('general.meta.subscribe')}
        </HpButton>
      )}
    </span>
  );
}
